import { Box, Grid } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import {
  FormDatePicker,
  FormTextField,
  GridCardContent,
} from "components/atoms";
import { AppTable, DocumentDetailHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { StatusHelper, TableHelper, TimeHelper } from "helpers";
import { useRequest } from "hooks/useRequest";
import {
  EPrepareStatus,
  IGetByIdRequest,
  IPaginateApiResponse,
  IPrepare,
  IPrepareDetail,
  IUpdatePrepareDocumentRequest,
} from "model";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PrepareActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import PrepareApiService from "services/PrepareApiService";
import { I18nTranslator, translations } from "utils/i18n";

const PrepareDetail = () => {
  const dispatch = useAppDispatch();
  const params = useParams<{
    id: string;
    prepareCode: string;
  }>();
  const data = useAppSelector(
    (state) => state.PrepareReducer.prepareDetail.data
  ) as IPaginateApiResponse<IPrepareDetail[]>;
  const action = useAppSelector(
    (state) => state.PrepareReducer.prepareDetail.action
  );

  useEffect(() => {
    _getPrepareDocumentDetail();
  }, []);

  const _getPrepareDocumentDetail = (pageNumber = 1) => {
    dispatch(
      PrepareActions.getPrepareDocumentDetail.request({
        prepareNoteHeaderId: params.id,
        pageNumber: pageNumber,
        pageSize: 100,
      })
    );
  };

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.receive.productId),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "description",
      headerName: I18nTranslator(translations.receive.productName),
      size: "medium",
      align: "left",
    }),
    TableHelper.createCell({
      field: "batchCode",
      headerName: I18nTranslator(translations.receive.batchCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "quantity",
      headerName: I18nTranslator(translations.receive.quantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "packQuantity",
      headerName: I18nTranslator(translations.receive.cartonS),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "packType",
      headerName: I18nTranslator(translations.receive.cartonType),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "packSize",
      headerName: I18nTranslator(translations.receive.cartonSpecification),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "packWeight",
      headerName: I18nTranslator(translations.receive.cartonWeight),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "productQuantity",
      headerName: I18nTranslator(translations.receive.oddQuantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "productUnit",
      headerName: I18nTranslator(translations.receive.uom),
      size: 100,
      align: "center",
    }),
    TableHelper.createCell({
      field: "userPrepare",
      headerName: I18nTranslator(translations.receive.preparedBy),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "userVerify",
      headerName: I18nTranslator(translations.receive.checkedBy),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "preparedQty",
      headerName: I18nTranslator(translations.receive.prepareQuantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "palletCode",
      headerName: I18nTranslator(translations.receive.pallet),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "deliveredQty",
      headerName: I18nTranslator(translations.receive.deliveredQuantity),
      size: "small",
      align: "right",
    }),

    TableHelper.createCell({
      field: "requireReturnQty",
      headerName: I18nTranslator(translations.receive.returnRequestQuantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "returnQty",
      headerName: I18nTranslator(translations.receive.returnedQuantity),
      size: "small",
      align: "right",
    }),
  ];

  return (
    <>
      <DetailHeader />
      <GridCardContent sx={{ flexDirection: "column" }}>
        <Box sx={{ width: "100%" }}>
          <Box sx={{ padding: "20px" }}>
            <AppTable<IPrepareDetail>
              loading={action.includes(
                PrepareActions.getPrepareDocumentDetail.requestName
              )}
              data={data}
              columns={COLUMNS}
              onChangePage={_getPrepareDocumentDetail}
              onSearch={() => {}}
              hideHeader
              hideSearch
              defaultHeaderTable={240}
              removeActionColumn
            />
          </Box>
        </Box>
      </GridCardContent>
    </>
  );
};

const DetailHeader = () => {
  const params = useParams<{
    prepareCode: string;
    id: string;
  }>();

  const dispatch = useAppDispatch();
  const action = useAppSelector((state) => state.ReceiveReducer.action);
  const [request] = useState({ id: params.id });

  const { loading, data } = useRequest<IGetByIdRequest, IPrepare>(
    request,
    PrepareApiService.getPrepareDocumentById
  );
  const formik = useFormik<IPrepare>({
    initialValues: data || ({} as IPrepare),
    enableReinitialize: true,
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      validateForm().then(() => {
        const request: IUpdatePrepareDocumentRequest = {
          ...(values as IUpdatePrepareDocumentRequest),
        };
        dispatch(PrepareActions.updatePrepareDocument.request(request));
      });
    },
  });

  return (
    <DocumentDetailHeader
      title={I18nTranslator(translations.receive.prepareDetail)}
      description={params.prepareCode}
      formik={formik}
      loading={false}
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.companyCode)}
            placeholder={"Nhập tên nhà cung cấp"}
            id="companyCode"
            disabled
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            required
            label={I18nTranslator(translations.receive.loCode)}
            id="doImportCode"
            disabled
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.companyName)}
            placeholder={"Nhập tên nhà cung cấp"}
            id="companyName"
            disabled
          />
        </Grid>
        <Grid xs={6} item>
          <FormDatePicker
            id="startDate"
            label={I18nTranslator(translations.receive.deliveryDate)}
            defaultValue={TimeHelper.formatDate(formik.values.deliveryDate)}
            disabled
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.country)}
            placeholder={"Nhập tên nhà cung cấp"}
            id="province"
            disabled
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.status)}
            id="status"
            disabled
            renderValue={() => {
              return StatusHelper.getPrepareStatusString(
                formik.values.status! as EPrepareStatus
              );
            }}
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.createdBy)}
            placeholder={I18nTranslator(translations.placeholder.creay)}
            id="userCreateFullName"
            disabled
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.userWarehouse)}
            id="userWareHouse"
            disabled
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.description)}
            placeholder={I18nTranslator(translations.placeholder.description)}
            id="description"
          />
        </Grid>

        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.userVerify)}
            placeholder={I18nTranslator(translations.placeholder.userVerify)}
            id="userReview"
          />
        </Grid>
      </Grid>
    </DocumentDetailHeader>
  );
};

export default PrepareDetail;
